import { type FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppRoute } from '@common/app/routes';

import { useJoinFriendsGroupMutation } from '@/api/queries/group';
import { defaultToastConfig } from '@/common/config/toast';
import {
  onGroupInvite,
  takePendingGroupInvite,
} from '@/common/lib/group-invite';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { setChangeGroup } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import friendsImage from '/img/icons/frens.png';

// Native app only: joins the group from an invite link once the user is logged
// in (Telegram does this at login through startParam)
const NativeGroupInvite: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.user.user?.id);
  const [joinGroup] = useJoinFriendsGroupMutation();

  const join = useCallback(async (): Promise<void> => {
    const uniqueGroupKey = takePendingGroupInvite();

    if (!uniqueGroupKey) return;

    try {
      const { group, changeGroup } = await joinGroup({
        uniqueGroupKey,
      }).unwrap();

      if (changeGroup) {
        // Home shows the "Switching groups" screen
        dispatch(setChangeGroup(changeGroup));
        navigate(AppRoute.MAIN);
      } else if (group) {
        navigate(AppRoute.FRIENDS);
      }
    } catch (error) {
      const message = (error as { data?: { message?: string } }).data?.message;
      showToast(
        t(message || 'Something went wrong, please try again'),
        { img: friendsImage },
        defaultToastConfig,
      );
    }
  }, [joinGroup, dispatch, navigate, t]);

  useEffect(() => {
    if (!userId) return;
    void join();

    return onGroupInvite(() => void join());
  }, [userId, join]);

  return null;
};

export { NativeGroupInvite };
